import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  Wallet, 
  Plus, 
  Trash2, 
  Edit, 
  Loader2,
  Printer,
  FileText,
  User,
  Building2,
  Calendar,
  Filter,
  Search
} from 'lucide-react';

const CATEGORIES = {
  personal: { label: 'شخصي', color: 'bg-purple-100 text-purple-700' },
  owner: { label: 'صاحب العمل', color: 'bg-blue-100 text-blue-700' },
  other: { label: 'أخرى', color: 'bg-stone-100 text-stone-700' }
};

const WithdrawalsPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [persons, setPersons] = useState([]);
  
  // Filters
  const [filterPerson, setFilterPerson] = useState('');
  const [filterDates, setFilterDates] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  
  // Dialogs
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [statementDialog, setStatementDialog] = useState(false);
  const [statement, setStatement] = useState(null);
  const [statementPerson, setStatementPerson] = useState('');
  
  const [formLoading, setFormLoading] = useState(false);

  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const fetchWithdrawals = useCallback(async () => {
    try {
      let url = `${API_URL}/api/withdrawals?start_date=${filterDates.start}&end_date=${filterDates.end}`;
      if (filterPerson) {
        url += `&person_name=${encodeURIComponent(filterPerson)}`;
      }
      const res = await axios.get(url, { headers: getAuthHeader() });
      setWithdrawals(res.data);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader, filterDates, filterPerson]);

  const fetchPersons = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/withdrawals/persons`, { headers: getAuthHeader() });
      setPersons(res.data);
    } catch (error) {
      console.error('Error fetching persons:', error);
    }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    fetchWithdrawals();
    fetchPersons();
  }, [fetchWithdrawals, fetchPersons]);

  // Calculate totals
  const totals = withdrawals.reduce((acc, w) => {
    acc.total += w.amount;
    acc[w.category] = (acc[w.category] || 0) + w.amount;
    return acc;
  }, { total: 0 });

  // Handlers
  const handleSaveWithdrawal = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      person_name: formData.get('person_name'),
      amount: parseFloat(formData.get('amount')),
      date: formData.get('date'),
      category: formData.get('category'),
      notes: formData.get('notes') || ''
    };

    try {
      if (selectedWithdrawal) {
        await axios.put(`${API_URL}/api/withdrawals/${selectedWithdrawal.id}`, data, { headers: getAuthHeader() });
        toast.success('تم تحديث المسحوبة');
      } else {
        await axios.post(`${API_URL}/api/withdrawals`, data, { headers: getAuthHeader() });
        toast.success('تم تسجيل المسحوبة');
      }
      setWithdrawalDialog(false);
      setSelectedWithdrawal(null);
      fetchWithdrawals();
      fetchPersons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteWithdrawal = async () => {
    try {
      await axios.delete(`${API_URL}/api/withdrawals/${selectedWithdrawal.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف المسحوبة');
      setDeleteDialog(false);
      setSelectedWithdrawal(null);
      fetchWithdrawals();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  const fetchStatement = async () => {
    if (!statementPerson) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/withdrawals/statement/${encodeURIComponent(statementPerson)}?start_date=${filterDates.start}&end_date=${filterDates.end}`,
        { headers: getAuthHeader() }
      );
      setStatement(res.data);
    } catch (error) {
      toast.error('خطأ في تحميل كشف الحساب');
    }
  };

  useEffect(() => {
    if (statementDialog && statementPerson) {
      fetchStatement();
    }
  }, [statementDialog, statementPerson, filterDates]);

  const handlePrintStatement = () => {
    if (!statement) return;

    const rows = statement.withdrawals.map(w => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${w.date}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">
          <span style="padding:2px 8px;border-radius:4px;font-size:12px;background:#f3f4f6">
            ${CATEGORIES[w.category]?.label || w.category}
          </span>
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:bold;color:#dc2626">
          ${fc(w.amount)} €
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${w.notes || '-'}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>كشف مسحوبات - ${statement.person_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #7c3aed; color: white; padding: 10px; text-align: right; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1 style="color:#7c3aed">كشف مسحوبات</h1>
        <h2>${statement.person_name}</h2>
        <p>الفترة: ${statement.start_date} — ${statement.end_date}</p>
        <p>عدد العمليات: ${statement.count}</p>

        <table>
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>الفئة</th>
              <th>المبلغ</th>
              <th>ملاحظات</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div style="margin-top:30px;padding:15px;background:#fee2e2;border-radius:8px;text-align:center">
          <span style="font-size:14px">إجمالي المسحوبات:</span>
          <span style="font-size:24px;font-weight:bold;margin-right:10px;color:#dc2626">${fc(statement.total_amount)} €</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div data-testid="withdrawals-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <Wallet className="w-6 h-6 text-purple-600" />
          المسحوبات
        </h1>
        <Button onClick={() => { setSelectedWithdrawal(null); setWithdrawalDialog(true); }} className="gap-1 bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4" /> تسجيل مسحوبة
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-red-600">إجمالي المسحوبات</p>
            <p className="text-2xl font-bold text-red-700">{fc(totals.total)} €</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-purple-600">شخصي</p>
            <p className="text-xl font-bold text-purple-700">{fc(totals.personal || 0)} €</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-blue-600">صاحب العمل</p>
            <p className="text-xl font-bold text-blue-700">{fc(totals.owner || 0)} €</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-stone-50 to-stone-100 border-stone-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-stone-600">أخرى</p>
            <p className="text-xl font-bold text-stone-700">{fc(totals.other || 0)} €</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label>من</Label>
              <Input
                type="date"
                value={filterDates.start}
                onChange={(e) => setFilterDates({ ...filterDates, start: e.target.value })}
                className="w-36"
              />
            </div>
            <div>
              <Label>إلى</Label>
              <Input
                type="date"
                value={filterDates.end}
                onChange={(e) => setFilterDates({ ...filterDates, end: e.target.value })}
                className="w-36"
              />
            </div>
            <div>
              <Label>الشخص</Label>
              <Select value={filterPerson || "all"} onValueChange={(v) => setFilterPerson(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="الكل" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">الكل</SelectItem>
                  {persons.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchWithdrawals} size="sm" variant="outline" className="gap-1">
              <Filter className="w-4 h-4" /> تصفية
            </Button>
            {filterPerson && (
              <Button 
                onClick={() => { setStatementPerson(filterPerson); setStatementDialog(true); }} 
                size="sm" 
                variant="outline" 
                className="gap-1 text-purple-600"
              >
                <FileText className="w-4 h-4" /> كشف حساب
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Withdrawals Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>التاريخ</TableHead>
                <TableHead>الشخص</TableHead>
                <TableHead>الفئة</TableHead>
                <TableHead className="text-center">المبلغ</TableHead>
                <TableHead>ملاحظات</TableHead>
                <TableHead className="text-center">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withdrawals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                    لا توجد مسحوبات مسجلة
                  </TableCell>
                </TableRow>
              ) : (
                withdrawals.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>{w.date}</TableCell>
                    <TableCell className="font-medium">{w.person_name}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${CATEGORIES[w.category]?.color}`}>
                        {CATEGORIES[w.category]?.label || w.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-bold text-red-600">{fc(w.amount)} €</TableCell>
                    <TableCell>{w.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setStatementPerson(w.person_name); setStatementDialog(true); }}
                          className="text-purple-600"
                          title="كشف حساب"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedWithdrawal(w); setWithdrawalDialog(true); }}
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedWithdrawal(w); setDeleteDialog(true); }}
                          className="text-red-600"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawalDialog} onOpenChange={setWithdrawalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedWithdrawal ? 'تعديل مسحوبة' : 'تسجيل مسحوبة جديدة'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveWithdrawal} className="space-y-4">
            <div>
              <Label>اسم الشخص *</Label>
              <Input 
                name="person_name" 
                defaultValue={selectedWithdrawal?.person_name} 
                placeholder="مثال: أحمد، المالك..."
                list="persons-list"
                required 
              />
              <datalist id="persons-list">
                {persons.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المبلغ (€) *</Label>
                <Input name="amount" type="number" step="0.01" defaultValue={selectedWithdrawal?.amount} required />
              </div>
              <div>
                <Label>التاريخ *</Label>
                <Input name="date" type="date" defaultValue={selectedWithdrawal?.date || format(new Date(), 'yyyy-MM-dd')} required />
              </div>
            </div>
            <div>
              <Label>الفئة</Label>
              <Select name="category" defaultValue={selectedWithdrawal?.category || 'personal'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">شخصي</SelectItem>
                  <SelectItem value="owner">صاحب العمل</SelectItem>
                  <SelectItem value="other">أخرى</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" defaultValue={selectedWithdrawal?.notes} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWithdrawalDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading} className="bg-purple-600 hover:bg-purple-700">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Statement Dialog */}
      <Dialog open={statementDialog} onOpenChange={setStatementDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>كشف مسحوبات - {statementPerson}</span>
              <Button variant="outline" size="sm" onClick={handlePrintStatement} className="gap-1">
                <Printer className="w-4 h-4" /> طباعة
              </Button>
            </DialogTitle>
          </DialogHeader>

          {statement && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center mb-4">
                <p className="text-sm text-red-600">إجمالي المسحوبات</p>
                <p className="text-3xl font-bold text-red-700">{fc(statement.total_amount)} €</p>
                <p className="text-xs text-red-500 mt-1">{statement.count} عملية</p>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>الفئة</TableHead>
                    <TableHead className="text-center">المبلغ</TableHead>
                    <TableHead>ملاحظات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.withdrawals.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.date}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${CATEGORIES[w.category]?.color}`}>
                          {CATEGORIES[w.category]?.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-bold text-red-600">{fc(w.amount)} €</TableCell>
                      <TableCell>{w.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذه المسحوبة؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWithdrawal} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WithdrawalsPage;
